import { createReducer } from '@reduxjs/toolkit'
import {
  clearPhotoshootDiscardRequest,
  clearPhotoshootResults,
  requestPhotoshootDiscard,
  setPhotoshootIsGenerating,
  setPhotoshootResultAt,
  setPhotoshootResults,
  setPhotoshootUploadedImage,
} from './actions'

export interface PhotoshootState {
  /** Sparse results, index-aligned with the photoshoot card grid. */
  results: (string | null)[]
  /**
   * `true` while a discard has been requested by the global Navbar back button
   * and the run component has not yet processed it. Acts as a one-shot signal.
   */
  discardRequested: boolean
  /**
   * `true` while the photoshoot run is generating results. Used by the global
   * Navbar to disable the Settings button during loading.
   */
  isGenerating: boolean
  /**
   * The product image uploaded on the form step (`blob:` URL) or `null`. Held
   * here so it survives a remount of the route-level `Photoshoots` component
   * when the user navigates back from the run step.
   */
  uploadedImageUrl: string | null
}

const initialState: PhotoshootState = {
  results: [],
  discardRequested: false,
  isGenerating: false,
  uploadedImageUrl: null,
}

export const photoshootReducer = createReducer(initialState, builder => {
  builder
    .addCase(setPhotoshootResults, (state, { payload }) => {
      state.results = payload
    })
    .addCase(setPhotoshootResultAt, (state, { payload }) => {
      const { index, result } = payload
      // Grow the array if needed so the index slot exists; preserves the
      // sparse-array contract used by selectors that read by index.
      while (state.results.length <= index) state.results.push(null)
      state.results[index] = result
    })
    .addCase(clearPhotoshootResults, state => {
      state.results = []
    })
    .addCase(requestPhotoshootDiscard, state => {
      state.discardRequested = true
    })
    .addCase(clearPhotoshootDiscardRequest, state => {
      state.discardRequested = false
    })
    .addCase(setPhotoshootIsGenerating, (state, { payload }) => {
      state.isGenerating = payload
    })
    .addCase(setPhotoshootUploadedImage, (state, { payload }) => {
      state.uploadedImageUrl = payload
    })
})
